import { Calendar, Clock, CheckCircle, Loader } from 'lucide-react';
import { useState, useEffect } from 'react';

const UpcomingTraining = () => {
  const [upcomingCourses, setUpcomingCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [email, setEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeMessage, setSubscribeMessage] = useState('');

  // Fetch upcoming training programs from API
  useEffect(() => {
    const fetchUpcomingTraining = async () => {
      const cachedData = localStorage.getItem("cachedUpcomingTrainings_v2");
      const cacheTimestamp = localStorage.getItem("upcomingTrainingsTimestamp_v2");
      const now = Date.now();

      // Use cache if available and not older than 1 hour (3600000 ms)
      if (cachedData && cacheTimestamp && now - parseInt(cacheTimestamp) < 3600000) {
        setUpcomingCourses(JSON.parse(cachedData));
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await fetch("https://admin.torchbearer.co.ke/api/training-programs/upcoming");

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const rawPrograms = data.data || (Array.isArray(data) ? data : []);
        const parsedPrograms = rawPrograms.map(program => {
          let parsedFeatures = [];
          if (program.features) {
            try {
              parsedFeatures = typeof program.features === 'string'
                ? JSON.parse(program.features)
                : Array.isArray(program.features)
                  ? program.features
                  : [];
            } catch (e) {
              console.error(`Failed to parse features for program ${program.id}:`, e);
              parsedFeatures = [];
            }
          }

          return {
            ...program,
            features: parsedFeatures,
          };
        });

        setUpcomingCourses(parsedPrograms);
        localStorage.setItem("cachedUpcomingTrainings_v2", JSON.stringify(parsedPrograms));
        localStorage.setItem("upcomingTrainingsTimestamp_v2", now.toString());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUpcomingTraining();
  }, []);


  // Format date range
  const formatDateRange = (startDate, endDate) => {
    const start = new Date(startDate);
    const end = new Date(endDate);

    const options = {
      month: 'long',
      day: 'numeric',
      year: start.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
    };

    if (start.toDateString() === end.toDateString()) {
      return start.toLocaleDateString('en-US', options);
    }

    if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
      return `${start.toLocaleDateString('en-US', { month: 'long', day: 'numeric' })} - ${end.getDate()}, ${start.getFullYear()}`;
    }

    return `${start.toLocaleDateString('en-US', options)} - ${end.toLocaleDateString('en-US', options)}`;
  };

  // Format time
  const formatTime = (startTime, endTime) => {
    const formatTimeString = (timeString) => {
      if (!timeString) return '';
      const time = new Date(`2000-01-01T${timeString}`);
      return time.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    };

    const start = formatTimeString(startTime);
    const end = formatTimeString(endTime);

    if (start && end) {
      return `${start} - ${end}`;
    }
    return start || end || 'Time TBA';
  };

  // Handle newsletter subscription
  const handleSubscribe = async (e) => {
    e.preventDefault();

    if (!email) {
      setSubscribeMessage('Please enter your email address');
      return;
    }

    setSubscribing(true);
    setSubscribeMessage('');

    try {
      const response = await fetch('/api/newsletter/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, source: 'training_page' }),
      });

      const data = await response.json();

      if (data.success) {
        setSubscribeMessage('✅ Successfully subscribed! You\'ll be notified about future training programs.');
        setEmail('');
      } else {
        setSubscribeMessage(data.message || 'Subscription failed. Please try again.');
      }
    } catch (err) {
      setSubscribeMessage('❌ Something went wrong. Please try again later.');
    } finally {
      setSubscribing(false);
    }
  };

  if (loading) {
    return (
      <section id="upcoming" className="pt-32 pb-20 md:pt-48 md:pb-32 bg-gradient-to-br from-amber-50 to-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Loader className="w-8 h-8 animate-spin mx-auto text-amber-600" />
            <p className="mt-4 text-gray-600">Loading upcoming training programs...</p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section id="upcoming" className="pt-32 pb-20 md:pt-48 md:pb-32 bg-gradient-to-br from-amber-50 to-amber-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md mx-auto">
              <p className="text-red-600 font-medium">Unable to load training programs</p>
              <p className="text-red-500 text-sm mt-2">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-4 bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700 transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="upcoming" className="pt-32 pb-20 md:pt-48 md:pb-32 scroll-mt-20 bg-gradient-to-br from-amber-50 to-amber-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Upcoming <span className="text-amber-600">Training Programs</span>
          </h2>
          <div className="w-24 h-1 bg-amber-600 mx-auto mb-8"></div>
          <p className="text-xl text-gray-700 max-w-4xl mx-auto">
            Explore our upcoming professional development programs and secure your spot today
          </p>
        </div>

        {upcomingCourses.length === 0 ? (
          <div className="text-center py-12">
            <div className="bg-white rounded-lg shadow-lg p-8 max-w-md mx-auto">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No Upcoming Training Programs</h3>
              <p className="text-gray-600 mb-6">
                We're currently planning our next batch of training programs.
                Subscribe to get notified when new programs are announced!
              </p>
            </div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {upcomingCourses.map((course) => (
              <div key={course.id} className="bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl transition-all duration-300 overflow-hidden flex flex-col group">
                {/* Top Image Section */}
                <div className="relative h-48 w-full overflow-hidden">
                  <img
                    src={course.image_url || '/images/default-training.png'}
                    alt={course.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-amber-600 text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider shadow-lg">
                      Training
                    </span>
                  </div>
                </div>

                <div className="p-6 flex-grow flex flex-col">
                  <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2 h-14">
                    {course.title}
                  </h3>

                  {course.description ? (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2 h-10 whitespace-pre-line">
                      {course.description}
                    </p>
                  ) : (
                    <div className="h-10 mb-4 bg-gray-50 rounded-lg animate-pulse w-3/4"></div>
                  )}

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center text-gray-600">
                      <Calendar className="w-4 h-4 mr-3 text-amber-500" />
                      <span className="text-sm font-medium">{formatDateRange(course.start_date, course.end_date)}</span>
                    </div>
                    <div className="flex items-center text-gray-600">
                      <Clock className="w-4 h-4 mr-3 text-amber-500" />
                      <span className="text-sm font-medium">{formatTime(course.start_time, course.end_time)}</span>
                    </div>

                    {course.features && course.features.length > 0 && (
                      <div className="flex flex-wrap gap-2 pt-1">
                        {course.features.slice(0, 3).map((feature, idx) => (
                          <span key={idx} className="bg-amber-50 text-amber-700 text-[10px] px-2 py-0.5 rounded-md border border-amber-100 font-medium">
                            {feature}
                          </span>
                        ))}
                      </div>
                    )}

                    <div className="pt-2">
                      {course.early_bird_price && parseFloat(course.early_bird_price) > 0 ? (
                        <div className="flex flex-col">
                          <span className="text-xs text-gray-500 line-through">
                            {course.currency || 'KES'} {parseFloat(course.price).toLocaleString()}
                          </span>
                          <span className="text-lg font-bold text-amber-600">
                            {course.currency || 'KES'} {parseFloat(course.early_bird_price).toLocaleString()}
                            <span className="text-xs ml-1 font-normal">(Early Bird)</span>
                          </span>
                        </div>
                      ) : (
                        <span className="text-lg font-bold text-amber-600">
                          {course.price && parseFloat(course.price) > 0
                            ? `${course.currency || 'KES'} ${parseFloat(course.price).toLocaleString()}`
                            : 'Free'}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="mt-auto">
                    {course.registration_link ? (
                      <a
                        href={course.registration_link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block w-full bg-amber-600 text-white text-center py-3 rounded-xl font-bold hover:bg-amber-700 transition-all shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
                      >
                        Register Now
                      </a>
                    ) : (
                      <button
                        className="block w-full bg-gray-900 text-white text-center py-3 rounded-xl font-bold hover:bg-gray-800 transition-all shadow-md"
                      >
                        View Details
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Newsletter Subscription */}
        <div className="mt-16 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">Want to be notified about future training?</h3>
          <form onSubmit={handleSubscribe} className="max-w-md mx-auto">
            <div className="flex">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email"
                className="flex-grow px-4 py-3 rounded-l-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                disabled={subscribing}
              />
              <button
                type="submit"
                disabled={subscribing}
                className="bg-amber-600 text-white px-6 py-3 rounded-r-lg font-bold hover:bg-amber-700 transition-colors disabled:bg-amber-400"
              >
                {subscribing ? 'Subscribing...' : 'Subscribe'}
              </button>
            </div>
            {subscribeMessage && (
              <p className={`mt-3 text-sm ${subscribeMessage.includes('✅') ? 'text-green-600' : 'text-red-600'}`}>
                {subscribeMessage}
              </p>
            )}
          </form>
        </div>
      </div>
    </section>
  );
};

export default UpcomingTraining;